/**
 * Admin Dashboard - Olympic Fit Wellness
 * Connected to live Supabase APIs
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Calendar, 
  DollarSign, 
  TrendingUp,
  ChevronRight,
  MapPin,
  Mail,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  UserPlus,
  Activity,
  Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

export default function AdminDashboard() {
  const { profile } = useAuth();
  const [stats, setStats] = useState({
    total_members: 0,
    active_bookings: 0,
    pending_contacts: 0,
    newsletter_subscribers: 0
  });
  const [recentMembers, setRecentMembers] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activitySummary, setActivitySummary] = useState({
    new_signups_week: 0,
    payments_week: 0,
    revenue_week: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load stats from API
      const statsData = await api.getAdminStats();
      setStats(statsData || {
        total_members: 0,
        active_bookings: 0,
        pending_contacts: 0,
        newsletter_subscribers: 0
      });

      // Load recent members
      const usersData = await api.getAdminUsers();
      setRecentMembers((usersData || []).slice(0, 5));

      // Load recent contacts
      const contactsData = await api.getAdminContacts();
      setRecentContacts((contactsData || []).slice(0, 5));

      // Load recent activity
      try {
        const activityData = await api.getRecentActivity();
        setRecentActivity(activityData?.activity || []);
        setActivitySummary(activityData?.summary || {
          new_signups_week: 0,
          payments_week: 0,
          revenue_week: 0
        });
      } catch (err) {
        console.log('Recent activity not available:', err);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const metrics = [
    { 
      label: 'Total Members', 
      value: stats.total_members.toLocaleString(), 
      change: '+12%', 
      positive: true, 
      icon: Users,
      href: '/admin/members'
    },
    { 
      label: 'Active Bookings', 
      value: stats.active_bookings.toLocaleString(), 
      change: '+8%', 
      positive: true, 
      icon: Calendar,
      href: '/admin/classes'
    },
    { 
      label: 'Pending Contacts', 
      value: stats.pending_contacts.toLocaleString(), 
      change: stats.pending_contacts > 0 ? 'Needs attention' : 'All clear', 
      positive: stats.pending_contacts === 0, 
      icon: Mail,
      href: '/admin/contacts'
    },
    { 
      label: 'Weekly Revenue', 
      value: `$${activitySummary.revenue_week.toLocaleString()}`, 
      change: `${activitySummary.payments_week} payments`, 
      positive: true, 
      icon: DollarSign,
      href: '/admin/analytics'
    }
  ];

  const quickActions = [
    { title: 'Manage Members', desc: 'View and manage member accounts', href: '/admin/members' },
    { title: 'CMS Pages', desc: 'Edit website content', href: '/admin/cms/pages' },
    { title: 'Media Library', desc: 'Upload and manage images', href: '/admin/cms/media' },
    { title: 'View Analytics', desc: 'Business performance metrics', href: '/admin/analytics' }
  ];

  const getStatusBadge = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400';
      case 'resolved': return 'bg-green-500/20 text-green-400';
      default: return 'bg-neutral-600 text-neutral-300';
    }
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'signup': return UserPlus;
      case 'payment': return CreditCard;
      default: return Activity;
    }
  };

  const getActivityColor = (color) => {
    switch(color) {
      case 'blue': return 'bg-blue-500/20 text-blue-400';
      case 'green': return 'bg-green-500/20 text-green-400';
      case 'yellow': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-neutral-600 text-neutral-300';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-white">
          Admin Dashboard
        </h1>
        <p className="text-neutral-400">Welcome back, {profile?.full_name?.split(' ')[0] || 'Admin'}! Here's what's happening with your business today.</p>
      </div>

      {/* Metrics Grid */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric, idx) => (
            <Link
              key={idx}
              to={metric.href}
              className="bg-neutral-800 rounded-xl p-6 border border-neutral-700 hover:border-neutral-600 transition-colors"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                  <metric.icon className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <span className={`flex items-center gap-1 text-sm font-medium ${
                  metric.positive ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {metric.change}
                  {metric.positive && typeof metric.change === 'string' && metric.change.includes('%') && (
                    <ArrowUpRight className="w-4 h-4" />
                  )}
                </span>
              </div>
              <div className="text-3xl font-heading font-bold text-white mb-1">{metric.value}</div>
              <div className="text-sm text-neutral-400">{metric.label}</div>
            </Link>
          ))}
        </div>
      )}

      {/* Recent Activity & Members Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Activity Feed - NEW */}
        <div className="bg-neutral-800 rounded-xl border border-neutral-700">
          <div className="flex items-center justify-between p-6 border-b border-neutral-700">
            <h2 className="text-xl font-heading font-bold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#3B82F6]" />
              Recent Activity
            </h2>
            <span className="text-xs text-neutral-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Live
            </span>
          </div>
          
          {recentActivity.length > 0 ? (
            <div className="divide-y divide-neutral-700 max-h-[400px] overflow-y-auto">
              {recentActivity.slice(0, 10).map((activity, idx) => {
                const IconComponent = getActivityIcon(activity.type);
                return (
                  <div key={idx} className="p-4 flex items-start gap-3 hover:bg-neutral-700/30 transition-colors">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.color)}`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-medium text-white truncate">{activity.title}</h4>
                        <span className="text-xs text-neutral-500 flex-shrink-0">{formatTimeAgo(activity.timestamp)}</span>
                      </div>
                      <p className="text-sm text-neutral-400 truncate">{activity.description}</p>
                      {activity.detail && (
                        <p className="text-xs text-neutral-500 truncate mt-0.5">{activity.detail}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Activity className="w-12 h-12 text-neutral-600 mx-auto mb-3" />
              <p className="text-neutral-400">No recent activity</p>
              <p className="text-neutral-500 text-sm mt-1">Activity will appear here as members sign up and make payments</p>
            </div>
          )}
          
          {/* Activity Summary Footer */}
          <div className="p-4 border-t border-neutral-700 bg-neutral-800/50">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-white">{activitySummary.new_signups_week}</div>
                <div className="text-xs text-neutral-400">New Members</div>
              </div>
              <div>
                <div className="text-lg font-bold text-white">{activitySummary.payments_week}</div>
                <div className="text-xs text-neutral-400">Payments</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-400">${activitySummary.revenue_week}</div>
                <div className="text-xs text-neutral-400">Revenue</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Members */}
        <div className="bg-neutral-800 rounded-xl border border-neutral-700">
          <div className="flex items-center justify-between p-6 border-b border-neutral-700">
            <h2 className="text-xl font-heading font-bold text-white">Recent Members</h2>
            <Link to="/admin/members" className="text-[#3B82F6] text-sm font-medium hover:underline flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentMembers.length > 0 ? (
            <div className="divide-y divide-neutral-700">
              {recentMembers.map((member) => (
                <div key={member.id} className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#3B82F6] flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {member.full_name?.[0] || member.email?.[0]?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-white">{member.full_name || 'Member'}</h3>
                    <p className="text-sm text-neutral-400">{member.email}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    member.membership_tier === 'elite' ? 'bg-yellow-500/20 text-yellow-400' :
                    member.membership_tier === 'wellness' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-neutral-600 text-neutral-300'
                  }`}>
                    {(member.membership_tier || 'member').toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-neutral-400">
              No members yet
            </div>
          )}
        </div>

        {/* Recent Contacts */}
        <div className="bg-neutral-800 rounded-xl border border-neutral-700">
          <div className="flex items-center justify-between p-6 border-b border-neutral-700">
            <h2 className="text-xl font-heading font-bold text-white">Recent Contacts</h2>
            <Link to="/admin/contacts" className="text-[#3B82F6] text-sm font-medium hover:underline flex items-center gap-1">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentContacts.length > 0 ? (
            <div className="divide-y divide-neutral-700">
              {recentContacts.map((contact) => (
                <div key={contact.submission_id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-white">{contact.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(contact.status)}`}>
                      {contact.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-400 truncate">{contact.subject || contact.message}</p>
                  <p className="text-xs text-neutral-500 mt-1">{contact.email}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-neutral-400">
              No contact submissions yet
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-neutral-800 rounded-xl border border-neutral-700 p-6">
        <h2 className="text-xl font-heading font-bold text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, idx) => (
            <Link
              key={idx}
              to={action.href}
              className="p-4 border border-neutral-700 rounded-lg hover:border-[#3B82F6] hover:bg-neutral-700/50 transition-all"
            >
              <h4 className="font-medium text-white mb-1">{action.title}</h4>
              <p className="text-sm text-neutral-400">{action.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
